import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, LogOut, User } from "lucide-react";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { state: { from: "/account" } });
    if (user) api.get("/orders").then(({ data }) => setOrders(data));
    document.title = "My Account — KKashigari India";
  }, [user, loading]);

  if (loading || !user) return <div className="py-40 text-center uplabel text-[var(--olive)]">Loading…</div>;

  const statusColor = (s) => (s === "paid" ? "text-[var(--emerald)]" : s === "failed" ? "text-[var(--maroon)]" : "text-[var(--olive)]");

  return (
    <div data-testid="account-page" className="max-w-[1100px] mx-auto px-5 lg:px-10 py-12 lg:py-16">
      <div className="flex items-start justify-between mb-12">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[var(--olive)] grid place-items-center text-[#FBF7DE]"><User className="w-6 h-6" /></div>
          <div>
            <h1 className="font-serif text-3xl text-[var(--bronze)]">{user.name}</h1>
            <p className="text-sm text-[var(--olive)]">{user.email}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="account-logout" className="flex items-center gap-2 uplabel text-[var(--bronze)] hover:text-[var(--maroon)] transition-colors">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      <h2 className="font-serif text-2xl text-[var(--bronze)] mb-6">Order History</h2>
      {orders.length === 0 ? (
        <div className="bg-[#F3EECB] p-12 text-center">
          <Package className="w-10 h-10 text-[var(--olive)] mx-auto" strokeWidth={1} />
          <p className="text-[var(--bronze)] mt-4">You haven't placed any orders yet.</p>
          <Link to="/shop" className="btn-primary inline-block px-6 py-3 uplabel mt-6">Start Shopping</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border border-[var(--line)] p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-[var(--line)]">
                <div>
                  <div className="font-serif text-lg text-[var(--bronze)]">{o.order_number}</div>
                  <div className="text-xs text-[var(--olive)]">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className={`uplabel ${statusColor(o.payment_status)}`}>{o.payment_status.replace("_", " ")}</span>
                  <span className="uplabel text-[var(--bronze)]">{o.fulfillment_status}</span>
                  <span className="font-medium text-[var(--bronze)]">{inr(o.total)}</span>
                </div>
              </div>
              <div className="flex gap-3 mt-4 flex-wrap">
                {o.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <img src={it.image} alt="" className="w-10 aspect-[3/4] object-cover" />
                    <span className="text-[var(--bronze)]">{it.name} <span className="text-[var(--olive)]">×{it.quantity}</span></span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
